import { ITokenInfo } from "~/types/global"
import { IRecommendedPerson, TOppNote } from "~/types/priority"
import { axiosInstance } from "~/utils/axios"
import { queryClient } from "~/utils/query-client"

export async function getPriorities(
  tokenInfo: ITokenInfo,
  id: string,
  bookmarked: boolean,
  linkedTenantId?: number,
  duration?: string
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${id}/opportunities`
  if (bookmarked) {
    url += "/starred"
  }
  const urlParams = new URLSearchParams()
  if (linkedTenantId) {
    urlParams.append("linked_tenant_id", linkedTenantId.toString())
  }
  if (duration) {
    urlParams.append("duration", duration)
  }
  const queryString = urlParams.toString()
  if (queryString) url += `?${queryString}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getPriority(
  tokenInfo: ITokenInfo,
  id: string,
  priority_id: string,
  bypassUserAccountChecks: boolean,
  linkedinUrl: string = "",
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${id}/opportunities/${priority_id}`
  const searchParams = new URLSearchParams()
  if (bypassUserAccountChecks) {
    searchParams.append(
      "bypassUserAccountChecks",
      bypassUserAccountChecks.toString()
    )
  }
  if (linkedTenantId) {
    searchParams.append("linked_tenant_id", linkedTenantId.toString())
  }
  if (linkedinUrl) searchParams.append("linkedinUrl", linkedinUrl)
  const queryString = searchParams.toString()
  if (queryString) url += `?${queryString}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSalesforceStamp(
  tokenInfo: ITokenInfo,
  account_id: string,
  id: string,
  type: string
) {
  const url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${account_id}/salesforce/${type}/${id}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getPriorityRecommendations(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  userLinkedindUrl: string = "",
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/peopleRecommendations`
  const queryString = new URLSearchParams()
  if (userLinkedindUrl) queryString.append("linkedinUrl", userLinkedindUrl)
  if (linkedTenantId)
    queryString.append("linked_tenant_id", linkedTenantId.toString())
  if (queryString.toString()) url += `?${queryString.toString()}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSaasPeopleRecommendations(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  userLinkedindUrl: string = "",
  linkedTenantId?: number
) {
  let url = `tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/saasPeopleRecommendations`
  const queryString = new URLSearchParams()
  if (userLinkedindUrl) queryString.append("linkedinUrl", userLinkedindUrl)
  if (linkedTenantId)
    queryString.append("linked_tenant_id", linkedTenantId.toString())
  if (queryString.toString()) url += `?${queryString.toString()}`

  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getSaasSourceDetails(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  linkedTenantId?: number
) {
  let url = `tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/saasSourceDetails`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getAdditionalPeople(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  bucketCategory: string,
  linkedTenantId?: number
) {
  const bucket = bucketCategory
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/additionalPeople/${bucket}`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }

  const res = await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
  return res?.data
}

export async function checkAdditionalPeople(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/checkAdditionalPeople`
  const queryString = new URLSearchParams()
  if (linkedTenantId)
    queryString.append("linked_tenant_id", linkedTenantId.toString())
  if (queryString.toString()) url += `?${queryString.toString()}`
  return await axiosInstance({
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}
export async function bookmarkPriority(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  bookmark: boolean,
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""
  let url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/star`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      action: bookmark ? "add" : "remove"
    }
  })
}

export async function markAsReadPriority(
  tokenInfo: ITokenInfo,
  accountId: number,
  priorityId: number,
  linkedTenantId?: number
) {
  const res = await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/read`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {}
  })
  if (res.status === 200) {
    const searchParams = new URLSearchParams(window.location.search)
    const isBookmarked = searchParams.get("bookmarked") === "true"
    queryClient.setQueryData(
      ["priorities", `${accountId}`, isBookmarked, linkedTenantId],
      (oldData: any) => {
        return {
          ...oldData,
          data: {
            ...oldData?.data,
            opportunities: oldData?.data?.opportunities?.map((item: any) => {
              return {
                ...item,
                is_read: item.id === priorityId || item.is_read
              }
            })
          }
        }
      }
    )
  }
  return res
}

export async function sharePriorities(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityIds: number[],
  emails: string[],
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/collaborator/opportunity_assignments/`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      collaborator_ids: [],
      new_collaborator_emails: emails,
      opportunity_ids: priorityIds
    }
  })
}

export async function saveTag(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  tagName: string,
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/user_opportunity_tags/create_tag`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      tag_name: tagName
    }
  })
}

export async function deleteTag(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  tagName: string,
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/user_opportunity_tags/delete_tag`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "DELETE",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      tag_name: tagName
    }
  })
}

export async function saveNotes(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  notes: TOppNote[]
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/user_opportunity_notes/update`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      notes: notes
    }
  })
}
export async function addNotes(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  notes: TOppNote[],
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/user_opportunity_notes/add_note`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      notes: notes
    }
  })
}
export async function deleteNotes(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  note_id: any,
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/user_opportunity_notes/delete_note`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      note_id: note_id
    }
  })
}

export async function addOrRemovePersonToOpp(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  person: IRecommendedPerson,
  action: "add" | "remove",
  linkedTenantId?: number
) {
  const prefix = tokenInfo.is_os_lite_user ? "os_lite/" : ""

  let url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/addPersonToOpp`

  if (action === "remove") {
    url = `${prefix}tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/removePersonFromOpp`
  }
  if (linkedTenantId) {
    url += "?linked_tenant_id=" + linkedTenantId
  }
  return await axiosInstance({
    method: action === "remove" ? "DELETE" : "POST",
    url: url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data:
      action === "remove" ? { person_id: person.id, type: person.type } : person
  })
}

export async function getSearchPeople(
  tokenInfo: ITokenInfo,
  accountId: string,
  priorityId: string,
  data: {
    keywords: string
    locationArray: string[]
    pageNo: number
    searchId: number
  },
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/opportunities/${priorityId}/peopleSearch`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    method: "POST",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: data
  })
}

export async function getEmailEnrichment({
  tokenInfo,
  accountId,
  personId,
  opportunityId,
  linkedin_url,
  linkedTenantId
}: {
  tokenInfo: ITokenInfo
  accountId?: string
  personId?: number
  opportunityId?: string
  linkedin_url?: string
  linkedTenantId?: number
}) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${accountId}/people/${personId}/email_enrichment`
  if (opportunityId) {
    url += `?opportunity_id=${opportunityId}`
  }
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  let obj = {
    method: "GET",
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  } as Record<string, any>
  if (linkedin_url) {
    url = `/tenants/${tokenInfo.tenant_id}/users/${tokenInfo.user_id}/pre_meetings/get_email`
    obj = {
      method: "POST",
      url,
      headers: {
        Authorization: `Bearer ${tokenInfo?.token}`
      },
      data: {
        linkedin_url: linkedin_url
      }
    }
  }

  return await axiosInstance(obj)
}

export async function getPersonDetails(
  tokenInfo: ITokenInfo,
  linkedinUrl?: string
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/persona/custom`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      linkedin_url: linkedinUrl
    }
  })
}

export async function getPersonPosts(
  tokenInfo: ITokenInfo,
  personId: number,
  cookiesMap: any
) {
  return await axiosInstance({
    method: "POST",
    url: `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/capture/persona/posts`,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: {
      personId: personId,
      cookiesMap
    }
  })
}

export async function getPriorityProposal(
  tokenInfo: ITokenInfo,
  {
    id,
    priority_id,
    linkedTenantId
  }: { id: string; priority_id: string; linkedTenantId?: number }
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/accounts/${id}/opportunities/${priority_id}/proposalV2`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    }
  })
}

export async function getFileDetails(
  tokenInfo: ITokenInfo,
  fileIds: number[],
  linkedTenantId?: number
) {
  let url = `/tenants/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/file_details`
  if (linkedTenantId) {
    url += `?linked_tenant_id=${linkedTenantId}`
  }
  return await axiosInstance({
    url,
    headers: {
      Authorization: `Bearer ${tokenInfo?.token}`
    },
    data: { doc_ids: fileIds },
    method: "POST"
  })
}
